// Mobile Menu Component for Fortisys Website
// Handles mobile navigation toggle and interactions

export class MobileMenu {
  constructor(toggleElement, menuElement, options = {}) {
    this.toggle = toggleElement;
    this.menu = menuElement;
    this.options = {
      animationDuration: 300,
      breakpoint: 992,
      ...options
    };

    this.isOpen = false;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupAccessibility();
  }

  bindEvents() {
    // Toggle button click
    this.toggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleMenu();
    });

    // Close on escape key
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);

    // Close when clicking outside
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);

    // Handle resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);

    // Handle mobile menu links
    const mobileLinks = this.menu.querySelectorAll('.header__mobile-link');
    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Close menu when a link is clicked
        this.closeMenu();
      });
    });
  }

  setupAccessibility() {
    // Set initial ARIA attributes
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-controls', this.menu.id || 'mobile-menu');

    if (!this.menu.id) {
      this.menu.id = 'mobile-menu';
    }

    // Set initial state
    this.menu.setAttribute('aria-hidden', 'true');
  }

  toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.isOpen = true;

    // Update classes
    this.menu.classList.add('header__mobile-menu--open');
    this.toggle.classList.add('header__mobile-toggle--active');

    // Update ARIA attributes
    this.toggle.setAttribute('aria-expanded', 'true');
    this.menu.setAttribute('aria-hidden', 'false');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus first menu item
    setTimeout(() => {
      const firstLink = this.menu.querySelector('.header__mobile-link');
      if (firstLink) {
        firstLink.focus();
      }
    }, 100);

    // Dispatch custom event
    this.dispatchEvent('mobileMenuOpen');
  }

  closeMenu() {
    this.isOpen = false;

    // Update classes
    this.menu.classList.remove('header__mobile-menu--open');
    this.toggle.classList.remove('header__mobile-toggle--active');

    // Update ARIA attributes
    this.toggle.setAttribute('aria-expanded', 'false');
    this.menu.setAttribute('aria-hidden', 'true');

    // Restore body scroll
    document.body.style.overflow = '';

    // Return focus to toggle button
    this.toggle.focus();

    // Dispatch custom event
    this.dispatchEvent('mobileMenuClose');
  }

  handleKeyDown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
    case 'Escape':
      event.preventDefault();
      this.closeMenu();
      break;

    case 'Tab':
      this.handleTabNavigation(event);
      break;

    case 'ArrowDown':
    case 'ArrowUp':
      this.handleArrowNavigation(event);
      break;
    }
  }

  handleTabNavigation(event) {
    const focusableElements = this.menu.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab (forward)
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  handleArrowNavigation(event) {
    event.preventDefault();

    const menuLinks = this.menu.querySelectorAll('.header__mobile-link');
    const currentIndex = Array.from(menuLinks).indexOf(document.activeElement);

    if (currentIndex === -1) return;

    let nextIndex;
    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex + 1 < menuLinks.length ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : menuLinks.length - 1;
    }

    menuLinks[nextIndex].focus();
  }

  handleOutsideClick(event) {
    if (!this.isOpen) return;

    // Check if click is outside both toggle and menu
    if (!this.toggle.contains(event.target) && !this.menu.contains(event.target)) {
      this.closeMenu();
    }
  }

  handleResize() {
    // Close menu on desktop breakpoint
    if (window.innerWidth >= this.options.breakpoint && this.isOpen) {
      this.closeMenu();
    }
  }

  dispatchEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: {
        menu: this,
        isOpen: this.isOpen
      }
    });

    this.toggle.dispatchEvent(event);
  }

  // Public API methods
  open() {
    if (!this.isOpen) {
      this.openMenu();
    }
  }

  close() {
    if (this.isOpen) {
      this.closeMenu();
    }
  }

  toggle() {
    this.toggleMenu();
  }

  destroy() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.handleResize);

    // Reset state
    if (this.isOpen) {
      this.closeMenu();
    }

    // Remove classes and attributes
    this.menu.classList.remove('header__mobile-menu--open');
    this.toggle.classList.remove('header__mobile-toggle--active');
    this.toggle.removeAttribute('aria-expanded');
    this.menu.removeAttribute('aria-hidden');
  }
}
